import React, { Component } from 'react'
import { View, ScrollView } from 'react-native'

import { Text } from '../../Atoms/Typography'
import CenteredSpinner from '../../Atoms/Spinner/CenteredSpinner'
import TranslatedText from '../../Atoms/TranslatedText'
import LoadAfterDelay from '../../Hocs/LoadAfterDelay'
import ValueChangeText from '../../Molecules/ValueChangeText'
import { thousandSeparator, percentage } from '../../../lib/formatters'
import { translate } from '../../../services/translate'
import { TableHeader, TableSection, Column, borderHack } from './Table'

import styled from 'styled-components/native'
import { lighten } from 'polished'
import theme from '../../../styles/theme'

const { gutterUnit } = theme

const Container = styled(View)`
  flex: 1;
  flex-direction: row;
`

const Header = styled(TableHeader)`
  borderLeftWidth: 0;
`

const Section = styled(TableSection)`
  borderLeftWidth: 0;
`

const BalanceTable = styled(View)`
  width: 63%;
`

const Row = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 28px;
`

const BigColumn = styled(Column)`
  width: 100%;
  height: 100%;
  justify-content: flex-start;
  borderRightColor: ${theme.colours.bordergrey};
  borderRightWidth: 1px;
`

const ColumnDesc = styled(Column)`
  width: 52%;
  height: 100%;
  justify-content: flex-start;
  borderRightColor: ${theme.colours.bordergrey};
  borderRightWidth: 1px;
`

const ColumnDescWithCost = styled(Column)`
  width: 52%;
  height: 100%;
  justify-content: space-between;
  borderRightColor: ${theme.colours.bordergrey};
  borderRightWidth: 1px;
`

const ColumnValue = styled(Column)`
  width: 24%;
  height: 100%;
  justify-content: flex-end;
  background-color: ${lighten(0.4, theme.colours.orange)};
  padding: 0 ${3 * gutterUnit}px 0 0 ;
`

const ColumnChange = styled(Column)`
  width: 24%;
  height: 100%;
`

const ColumnRisk = styled(Column)`
  width: 60%;
  height: 100%;
  borderRightColor: ${theme.colours.bordergrey};
  borderRightWidth: 1px;
`

const ColumnCA = styled(Column)`
  width: 40%;
  height: 100%;
`

const RiskTable = styled(View)`
  width: 37%;
`

const Spacer = styled(View)`
  height: 1px;
`

const formatValue = (val) => {
  if (val === 0) {
    return '-'
  }
  if (val < 0) {
    return `(${thousandSeparator(val * -1)})`
  }
  return thousandSeparator(val)
}

const formatRisk = (val, deduction) => {
  if (val === 0) { return '-' }
  if (val === -1) { return deduction }
  return percentage(val, 0)
}

const formatPercentage = (val, dp) => {
  if (val === 0) {
    return '-'
  }
  return percentage(val, dp)
}
class FinancialGroupBalanceSheet extends Component {
  shouldComponentUpdate (nextProps, nextState) {
    if (nextProps.loaded && !this.props.loaded) {
      return true
    }
    if (!nextProps.active) {
      return false
    }
    return true
  }

  render () {
    const { loaded, data, round, denomination, fundsMgmtIncomeEnabled, investmentExperienceEnabled, offshoreBankEnabled, creditCardsEnabled, institutionalBankingEnabled } = this.props
    const rounding = data.netAssets - data.operationalRisk - data.creditRisk
    return (
      <ScrollView>
        <Container>
          <BalanceTable>
            <Header>
              <Row>
                <BigColumn style={{ borderRightWidth: 0 }}><TranslatedText size='s' bold white uppercase value='ourFinancials.groupBalanceSheet.screenHeader' default='GROUP BALANCE SHEET & CAPITAL ALLOCATION' /></BigColumn>
              </Row>
              <Row>
                <ColumnDesc style={{ borderRightWidth: 0 }}><Text size='s' medium white>{denomination}</Text></ColumnDesc>
                <ColumnChange style={{ backgroundColor: 'transparent' }}><Text size='s' medium white uppercase>{`${translate('general.year', 'YEAR')} ${round - 1}`}</Text></ColumnChange>
                <ColumnChange><TranslatedText size='s' medium white uppercase value='general.change' default='CHANGE' /></ColumnChange>
              </Row>
            </Header>
            {loaded ? (
              <>
                <Section>
                  <Row>
                    <ColumnDesc><Text size='s' bold>ASSETS</Text></ColumnDesc>
                    <ColumnValue><Text> </Text></ColumnValue>
                    <ColumnChange><Text> </Text></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.homeLoans' default='Mortgages' /></ColumnDesc>
                    <ColumnValue><Text>{formatValue(data.homeLoans)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.homeLoansChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.businessLoans' default='Business Lending' /></ColumnDesc>
                    <ColumnValue style={!institutionalBankingEnabled && !creditCardsEnabled ? borderHack : {}}><Text>{formatValue(data.businessLoans)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.businessLoansChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  {creditCardsEnabled && (
                    <Row>
                      <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.creditCards' default='Credit Cards' /></ColumnDesc>
                      <ColumnValue style={!institutionalBankingEnabled ? borderHack : {}}><Text>{formatValue(data.creditCards)}</Text></ColumnValue>
                      <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.creditCardsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                    </Row>
                  )}
                  {institutionalBankingEnabled && (
                    <Row>
                      <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.institutionalBanking' default='Institutional Banking' /></ColumnDesc>
                      <ColumnValue style={borderHack}><Text>{formatValue(data.institutionalBanking)}</Text></ColumnValue>
                      <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.institutionalBankingChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                    </Row>
                  )}
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.totalLendingAssets' default='TOTAL LENDING ASSETS' /></ColumnDesc>
                    <ColumnValue><Text bold>{formatValue(data.totalLendingAssets)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} bold value={data.totalLendingAssetsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.cash' default='Cash' /></ColumnDesc>
                    <ColumnValue><Text>{formatValue(data.cash)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.cashChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.fixedAssets' default='Fixed Assets' /></ColumnDesc>
                    <ColumnValue><Text>{formatValue(data.fixedAssets)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.fixedAssetsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  {fundsMgmtIncomeEnabled && (
                    <Row>
                      <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.wealthManagement' default='Wealth Management' /></ColumnDesc>
                      <ColumnValue style={(!offshoreBankEnabled && !investmentExperienceEnabled) ? borderHack : {}}><Text>{formatValue(data.wealthAssets)}</Text></ColumnValue>
                      <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.wealthAssetsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                    </Row>
                  )}
                  {offshoreBankEnabled && (
                    <Row>
                      <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.offshoreBank' default='Offshore Bank' /></ColumnDesc>
                      <ColumnValue style={!investmentExperienceEnabled ? borderHack : {}}><Text>{formatValue(data.offshoreBank)}</Text></ColumnValue>
                      <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.offshoreBankChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                    </Row>
                  )}
                  {investmentExperienceEnabled && (
                    <Row>
                      <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.investments' default='Investments' /></ColumnDesc>
                      <ColumnValue style={borderHack}><Text>{formatValue(data.investments)}</Text></ColumnValue>
                      <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.investmentsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                    </Row>
                  )}
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.totalOtherAssets' default='TOTAL OTHER ASSETS' /></ColumnDesc>
                    <ColumnValue><Text bold>{formatValue(data.totalOtherAssets)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} bold value={data.totalOtherAssetsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.totalAssets' default='TOTAL ASSETS' /></ColumnDesc>
                    <ColumnValue style={borderHack}><Text bold>{formatValue(data.totalAssets)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} bold value={data.totalAssetsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnDescWithCost>
                      <TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.liabilities' default='LIABILITIES' />
                      <TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.cost' default='COST' />
                    </ColumnDescWithCost>
                    <ColumnValue><Text> </Text></ColumnValue>
                    <ColumnChange><Text> </Text></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDescWithCost>
                      <TranslatedText size='s' value='ourFinancials.groupBalanceSheet.depositsTrans' default='Deposits - Transaction' />
                      <Text size='s'>{formatPercentage(data.costOfTransaction)}</Text>
                    </ColumnDescWithCost>
                    <ColumnValue><Text>{formatValue(data.transactions)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.transactionsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDescWithCost>
                      <TranslatedText size='s' value='ourFinancials.groupBalanceSheet.depositsSavings' default='Deposits - Savings' />
                      <Text size='s'>{formatPercentage(data.costOfSavings)}</Text>
                    </ColumnDescWithCost>
                    <ColumnValue><Text>{formatValue(data.savings)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.savingsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDescWithCost>
                      <TranslatedText size='s' value='ourFinancials.groupBalanceSheet.wholesaleFunding' default='Wholesale Funding' />
                      <Text size='s'>{formatPercentage(data.costOfWSF)}</Text>
                    </ColumnDescWithCost>
                    <ColumnValue><Text>{formatValue(data.wholesaleFunding)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.wholesaleFundingChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDescWithCost>
                      <TranslatedText size='s' value='ourFinancials.groupBalanceSheet.centralBank' default='Central Bank Facility' />
                      <Text size='s'>{formatPercentage(data.costOfCBF)}</Text>
                    </ColumnDescWithCost>
                    <ColumnValue><Text>{formatValue(data.centralBankFunding)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.centralBankFundingChange} isCost changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.totalLiabilities' default='TOTAL LIABILITIES' /></ColumnDesc>
                    <ColumnValue style={borderHack}><Text bold>{formatValue(data.totalLiabilities)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} bold value={data.totalLiabilitiesChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.netAssets' default='NET ASSETS' /></ColumnDesc>
                    <ColumnValue><Text bold>{formatValue(data.netAssets)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} bold value={data.netAssetsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.shareholderEquity' default={'SHAREHOLDERS\' EQUITY'} /></ColumnDesc>
                    <ColumnValue><Text> </Text></ColumnValue>
                    <ColumnChange><Text> </Text></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.retainedEarnings' default='Retained Earnings' /></ColumnDesc>
                    <ColumnValue><Text>{formatValue(data.retainedEarnings)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.retainedEarningsChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' value='ourFinancials.groupBalanceSheet.shareCapital' default='Share Capital' /></ColumnDesc>
                    <ColumnValue style={borderHack}><Text>{formatValue(data.shareCapital)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} value={data.shareCapitalChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnDesc><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.totalShareholdersEquity' default={'TOTAL SHAREHOLDERS\' EQUITY'} /></ColumnDesc>
                    <ColumnValue><Text bold>{formatValue(data.netAssets)}</Text></ColumnValue>
                    <ColumnChange><ValueChangeText removePlusSign largeThreshold={10000} dp={0} bold value={data.totalShareHoldersEquityChange} isCost={false} changeUnit='%' dashIfZero /></ColumnChange>
                  </Row>
                </Section>
              </>) : <CenteredSpinner />}
          </BalanceTable>
          <RiskTable>
            <Header>
              <Row style={{ height: 56 }}>
                <ColumnRisk><TranslatedText size='s' white medium center uppercase value='ourFinancials.groupBalanceSheet.riskHeader' default={'RISK WEIGHT /\n DEDUCTION'} /></ColumnRisk>
                <ColumnCA><TranslatedText size='s' white medium center uppercase value='ourFinancials.groupBalanceSheet.capitalHeader' default='CAPITAL ALLOCATION' /></ColumnCA>
              </Row>
            </Header>
            {loaded ? (
              <>
                <Section>
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text>{formatRisk(data.homeLoansCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                    <ColumnCA><Text>{formatValue(data.homeLoansCA)}</Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text>{formatRisk(data.businessLoansCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                    <ColumnCA><Text>{formatValue(data.businessLoansCA)}</Text></ColumnCA>
                  </Row>
                  {creditCardsEnabled && (
                    <Row>
                      <ColumnRisk><Text>{formatRisk(data.creditCardsCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                      <ColumnCA><Text>{formatValue(data.creditCardsCA)}</Text></ColumnCA>
                    </Row>
                  )}
                  {institutionalBankingEnabled && (
                    <Row>
                      <ColumnRisk><Text>{formatRisk(data.institutionalBankingCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                      <ColumnCA><Text>{formatValue(data.institutionalBankingCA)}</Text></ColumnCA>
                    </Row>
                  )}
                </Section>
                <Section>
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text bold>{formatValue(data.totalLendingAssetsCA)}</Text></ColumnCA>
                  </Row>
                  <Spacer />
                  <Row>
                    <ColumnRisk><Text>{formatRisk(data.cashCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                    <ColumnCA><Text>{formatValue(data.cashCA)}</Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text>{formatRisk(data.fixedAssetsCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                    <ColumnCA><Text>{formatValue(data.fixedAssetsCA)}</Text></ColumnCA>
                  </Row>
                  {fundsMgmtIncomeEnabled && (
                    <Row>
                      <ColumnRisk><Text>{formatRisk(data.wealthAssetsCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                      <ColumnCA><Text>{formatValue(data.wealthAssetsCA)}</Text></ColumnCA>
                    </Row>
                  )}
                  {offshoreBankEnabled && (
                    <Row>
                      <ColumnRisk><Text>{formatRisk(data.offshoreBankCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                      <ColumnCA><Text>{formatValue(data.offshoreBankCA)}</Text></ColumnCA>
                    </Row>
                  )}
                  {investmentExperienceEnabled && (
                    <Row>
                      <ColumnRisk><Text>{formatRisk(data.investmentsCT, translate('ourFinancials.groupBalanceSheet.deduction', 'Deduction'))}</Text></ColumnRisk>
                      <ColumnCA><Text>{formatValue(data.investmentsCA)}</Text></ColumnCA>
                    </Row>
                  )}
                </Section>
                <Section>
                  <Row>
                    <ColumnRisk style={{ justifyContent: 'flex-start' }}><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.creditRisk' default='CREDIT RISK' /></ColumnRisk>
                    <ColumnCA><Text bold>{formatValue(data.creditRisk)}</Text></ColumnCA>
                  </Row>
                  <Spacer />
                  <Row>
                    <ColumnRisk style={{ justifyContent: 'flex-start' }}><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.operationalRisk' default='OPERATIONAL RISK' /></ColumnRisk>
                    <ColumnCA><Text bold>{formatValue(data.operationalRisk)}</Text></ColumnCA>
                  </Row>
                  <Spacer />
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Spacer />
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Spacer />
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Spacer />
                  <Row>
                    <ColumnRisk><Text> </Text></ColumnRisk>
                    <ColumnCA><Text> </Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk><Text colour={theme.colours.grey}>{rounding > 20 ? 'ROUNDING' : ''}</Text></ColumnRisk>
                    <ColumnCA><Text colour={theme.colours.grey}>{rounding > 20 ? rounding : ''}</Text></ColumnCA>
                  </Row>
                  <Row>
                    <ColumnRisk style={{ justifyContent: 'flex-start' }}><TranslatedText size='s' bold colour={theme.colours.blue} uppercase value='ourFinancials.groupBalanceSheet.capitalRatio' default='CAPITAL RATIO' /></ColumnRisk>
                    <ColumnCA><Text bold colour={theme.colours.blue}>{percentage(data.cet1Ratio, 2)}</Text></ColumnCA>
                  </Row>
                </Section>
                <Section>
                  <Row>
                    <ColumnRisk style={{ justifyContent: 'flex-start' }}><TranslatedText size='s' bold uppercase value='ourFinancials.groupBalanceSheet.totalCapital' default='TOTAL CAPITAL' /></ColumnRisk>
                    <ColumnCA><Text bold>{formatValue(data.netAssets)}</Text></ColumnCA>
                  </Row>
                </Section>
              </>) : <CenteredSpinner />}
          </RiskTable>
        </Container>
      </ScrollView>
    )
  }
}

export default LoadAfterDelay(FinancialGroupBalanceSheet, { delay: 100 })
